import type { ServerMessage } from '@elena/shared';
import { session } from './session';

export type ConnectionState = 'connecting' | 'online' | 'offline';

/** WebSocket connection to the Elena server with automatic reconnect. */
export class ElenaSocket {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private closed = false;
  private retryTimer: number | undefined;
  private pingTimer: number | undefined;

  constructor(
    private readonly onMessage: (msg: ServerMessage) => void,
    private readonly onState: (state: ConnectionState) => void,
  ) {}

  connect(): void {
    this.closed = false;
    this.onState('connecting');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${proto}://${location.host}/ws?token=${encodeURIComponent(session.token() ?? '')}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      ws.send(
        JSON.stringify({
          type: 'hello',
          deviceId: session.deviceId(),
          kind: session.origin() === 'phone' ? 'mobile' : 'web',
          name: session.origin() === 'phone' ? 'Telefon (web)' : 'Web',
          capabilities: [],
        }),
      );
      this.pingTimer = window.setInterval(() => this.send({ type: 'ping' }), 25_000);
    };
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(String(event.data)) as ServerMessage;
        if (msg.type === 'welcome') this.onState('online');
        this.onMessage(msg);
      } catch {
        // ignore malformed frames
      }
    };
    ws.onclose = () => {
      window.clearInterval(this.pingTimer);
      this.onState('offline');
      if (this.closed) return;
      const delay = Math.min(15_000, 800 * 2 ** Math.min(this.attempt, 5));
      this.attempt += 1;
      this.retryTimer = window.setTimeout(() => this.connect(), delay);
    };
  }

  send(msg: unknown): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  close(): void {
    this.closed = true;
    window.clearTimeout(this.retryTimer);
    window.clearInterval(this.pingTimer);
    this.ws?.close();
  }
}
